import { ROUTES } from '../../constants/routes';
import { PERMISSION } from '../../shared/utils/permissions';
import {
  canViewTab,
  type DashboardSection,
  ORGANIZATIONS_ROLES_SECTION,
  SUPPORT_SECTION,
} from './dashboardSections';

export interface NavVisibilityContext {
  has: (permission: string) => boolean;
  /** Set when the user is limited to one organization (owner or member). */
  organization: { id: string; isOwner: boolean } | null;
}

export interface DashboardNavItem {
  labelKey: string;
  /** Path segment under /dashboard (all routes are single segments). */
  route: string;
  /** Further path segments that belong to this section (tabs with their own URL). */
  aliases?: string[];
  icon: string;
  /** Header title for this section; omitted sections fall back to the default title. */
  titleKey?: string;
  /** Permission gate; omitted items are always visible. */
  when?: (ctx: NavVisibilityContext) => boolean;
}

/**
 * Settings holds only the Monobank token, which belongs to an organization's
 * owner; anyone else (platform staff included) would open an empty page.
 */
export const canSeeSettings = ({ has, organization }: NavVisibilityContext): boolean =>
  has('settings.view') && Boolean(organization?.isOwner);

/** A tabbed section shows while any of its tabs is viewable. */
export const sectionNavItemVisible = (section: DashboardSection, ctx: NavVisibilityContext): boolean =>
  section.tabs.some((tab) => canViewTab(tab, ctx));

/**
 * A tabbed section's single sidebar entry: it lands on the first tab, owns
 * every tab's URL, and shows while any tab is viewable.
 */
const sectionNavItem = (section: DashboardSection): DashboardNavItem => ({
  labelKey: section.labelKey,
  titleKey: section.titleKey,
  icon: section.icon,
  route: section.tabs[0].route,
  aliases: section.tabs.slice(1).map((tab) => tab.route),
  when: (ctx) => sectionNavItemVisible(section, ctx),
});

/**
 * Single source of truth for the dashboard sections: the sidebar renders
 * from it and the header title is derived from it, so the two can't drift.
 */
export const DASHBOARD_NAV_ITEMS: DashboardNavItem[] = [
  {
    labelKey: 'dashboard.sidebar.metrics',
    route: ROUTES.METRICS,
    icon: 'analytics',
    titleKey: 'dashboard.pageTitles.metrics',
    when: ({ has }) => has(PERMISSION.METRICS_VIEW),
  },
  {
    labelKey: 'dashboard.sidebar.stations',
    route: ROUTES.DASH_STATIONS,
    icon: 'ev_station',
    titleKey: 'dashboard.pageTitles.stations',
    when: ({ has }) => has(PERMISSION.STATIONS_VIEW),
  },
  {
    labelKey: 'dashboard.sidebar.sessions',
    route: ROUTES.DASH_SESSIONS,
    icon: 'electric_car',
    titleKey: 'dashboard.pageTitles.sessions',
    when: ({ has }) => has(PERMISSION.SESSIONS_VIEW),
  },
  {
    labelKey: 'dashboard.sidebar.transactions',
    route: ROUTES.DASH_TRANSACTIONS,
    icon: 'receipt_long',
    titleKey: 'dashboard.pageTitles.transactions',
    when: ({ has }) => has(PERMISSION.TRANSACTIONS_VIEW),
  },
  {
    labelKey: 'dashboard.sidebar.users',
    route: ROUTES.DASH_USERS,
    icon: 'group',
    titleKey: 'dashboard.pageTitles.users',
    when: ({ has }) => has(PERMISSION.USERS_VIEW),
  },
  sectionNavItem(ORGANIZATIONS_ROLES_SECTION),
  {
    labelKey: 'dashboard.sidebar.team',
    route: ROUTES.DASH_TEAM,
    icon: 'groups',
    titleKey: 'dashboard.pageTitles.team',
    // Only an organization's owner manages its team.
    when: ({ organization }) => Boolean(organization?.isOwner),
  },
  {
    labelKey: 'dashboard.sidebar.premium',
    route: ROUTES.DASH_PREMIUM,
    icon: 'workspace_premium',
    titleKey: 'dashboard.pageTitles.premium',
    // Organization owners and members with premium.manage. The page is always
    // the caller's own organization, so it's hidden from anyone outside one:
    // platform admins pass the permission check (wildcard) but manage premium
    // per organization from the Organizations page.
    when: ({ has, organization }) => has(PERMISSION.PREMIUM_MANAGE) && organization !== null,
  },
  sectionNavItem(SUPPORT_SECTION),
  {
    labelKey: 'dashboard.sidebar.system',
    route: ROUTES.DASH_SYSTEM,
    icon: 'database',
    titleKey: 'dashboard.pageTitles.system',
    // Platform owner only: system.manage is never org-grantable.
    when: ({ has }) => has(PERMISSION.SYSTEM_MANAGE),
  },
  {
    labelKey: 'dashboard.sidebar.settings',
    route: ROUTES.DASH_SETTINGS,
    icon: 'settings',
    when: canSeeSettings,
  },
];

/** Whether a path segment under /dashboard belongs to this section. */
export const navItemOwnsSegment = (item: DashboardNavItem, segment: string): boolean =>
  item.route === segment || Boolean(item.aliases?.includes(segment));

/**
 * Header titles for sections reached from the header rather than the nav list:
 * notifications open from the bell, the profile from the sidebar's footer.
 */
const OFF_NAV_TITLE_KEYS: Record<string, string> = {
  [ROUTES.NOTIFICATIONS]: 'dashboard.pageTitles.notifications',
  [ROUTES.DASH_PROFILE]: 'dashboard.pageTitles.profile',
};

/**
 * Header title key for the current location, derived from the nav config.
 * Matches on the first path segment under /dashboard, so nested pages
 * (e.g. /dashboard/sessions/:id/metrics) keep their section title.
 */
export const getPageTitleKey = (pathname: string): string => {
  if (/\/users\/[^/]+/.test(pathname)) {
    return 'dashboard.pageTitles.userDetails';
  }
  const segment = pathname.split('/')[2];
  const item = DASHBOARD_NAV_ITEMS.find((nav) => navItemOwnsSegment(nav, segment));
  return item?.titleKey ?? OFF_NAV_TITLE_KEYS[segment] ?? 'dashboard.pageTitles.default';
};
